<script setup lang="ts">
/**
 * The contextual assistant's card (Figma `Contextual assistant message`, 320 wide):
 * white, radius 24 with a 2px bottom-right corner, drop shadow 0 0 20 rgba(26,33,51,.1),
 * padding 16 / 24 right, Inter 14/1.5 body, Gilroy 14 chips, AIRA's ball peeking from the
 * bottom-right corner, × top-right. Sits above AIRA's floating ball.
 */
import { useI18n } from 'vue-i18n'
import airaBlob from '@/assets/style-space/playground/aira-blob.png'
import iconClose from '@/ui/icons/main/close.svg?url'
import { useAiraContextualStore } from './useAiraContextualStore'
import { useAiraContextualCommands } from './useAiraContextualCommands'

const store = useAiraContextualStore()
const { run } = useAiraContextualCommands()
const { t } = useI18n()
</script>

<template>
  <Transition name="fair-aira-ctx">
    <aside
      v-if="store.current"
      :key="store.current.id"
      class="fair-aira-ctx"
      role="status"
      :aria-label="t('aira_contextual.label')"
      data-testid="aira-contextual-card"
    >
      <p
        v-if="store.current.title"
        class="fair-aira-ctx__title"
      >
        {{ store.current.title }}
      </p>
      <p class="fair-aira-ctx__text">
        {{ store.current.text }}
      </p>
      <p
        v-if="store.current.hint"
        class="fair-aira-ctx__hint"
      >
        {{ store.current.hint }}
      </p>
      <div
        v-if="store.current.highlight"
        class="fair-aira-ctx__highlight"
      >
        {{ store.current.highlight.text }}
      </div>
      <div
        v-if="store.current.buttons.length"
        class="fair-aira-ctx__buttons"
      >
        <button
          v-for="(button, index) in store.current.buttons"
          :key="index"
          type="button"
          class="fair-aira-ctx__button"
          @click="run(store.current.id, button)"
        >
          {{ button.label }}
        </button>
      </div>

      <img
        class="fair-aira-ctx__blob"
        :src="airaBlob"
        alt=""
        aria-hidden="true"
      >

      <button
        v-if="store.current.dismissible !== false"
        type="button"
        class="fair-aira-ctx__close"
        :aria-label="t('aira_contextual.close')"
        @click="store.dismiss(store.current.id)"
      >
        <img
          :src="iconClose"
          alt=""
          width="16"
          height="16"
        >
      </button>
    </aside>
  </Transition>
</template>

<style scoped>
.fair-aira-ctx {
  position: fixed;
  z-index: 1090;
  right: 24px;
  /* above AIRA's ball (24 + ~80) */
  bottom: 120px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 320px;
  padding: 16px 24px 16px 16px;
  border-radius: 24px 24px 2px 24px;
  background: var(--color-bg-surface, #fff);
  filter: drop-shadow(0 0 10px rgba(26, 33, 51, 0.1));
}

.fair-aira-ctx__title,
.fair-aira-ctx__text,
.fair-aira-ctx__hint {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: break-word;
}

.fair-aira-ctx__title {
  color: var(--fair-rd-text-primary, #1a2133);
}

.fair-aira-ctx__text {
  color: var(--fair-rd-text-secondary, #5a6172);
}

.fair-aira-ctx__hint {
  color: var(--fair-rd-text-muted, #8a90a0);
}

.fair-aira-ctx__highlight {
  padding: 12px;
  border-radius: 8px;
  background: var(--fair-rd-fill-chip, rgba(26, 33, 51, 0.06));
  color: var(--fair-rd-text-secondary, #5a6172);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

.fair-aira-ctx__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

/* type/button: Gilroy Medium 14, ls .28, in a 22px chip */
.fair-aira-ctx__button {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: var(--fair-rd-fill-chip, rgba(26, 33, 51, 0.06));
  color: var(--fair-rd-text-secondary, #5a6172);
  font-family: 'Gilroy', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.28px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
}

.fair-aira-ctx__button:hover {
  background: rgba(26, 33, 51, 0.1);
}

.fair-aira-ctx__blob {
  position: absolute;
  right: -40px;
  bottom: -40px;
  width: 80px;
  height: 80px;
  pointer-events: none;
}

.fair-aira-ctx__close {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 8px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
}

.fair-aira-ctx-enter-active,
.fair-aira-ctx-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fair-aira-ctx-enter-from,
.fair-aira-ctx-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 1023px) {
  .fair-aira-ctx {
    right: 16px;
    left: 16px;
    /* above the mobile bottom nav and the ball */
    bottom: 160px;
    width: auto;
  }
}
</style>
